import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { closePanel, setError } from "../stores/aiChatSlice";
import { useAIHotkey } from "../hooks/useAIHotkey";
import { useIsAIEnabled } from "../hooks/useIsAIEnabled";
import AIMessageList from "./AIMessageList";
import AIComposer from "./AIComposer";
import AIPanelHeader from "./AIPanelHeader";
import { VoiceProvider } from "./VoiceProvider";
import VoiceCallOverlay from "./VoiceCallOverlay";

// ---------------------------------------------------------------------------
// AIChatPanel — right-side floating drawer. Mounted once at AppLayout root
// so it's reachable from every screen (Cursor pattern).
//
// Inline-styled so it doesn't depend on the Tailwind / shadcn theme. The
// container sits above content with z-index: 50.
// ---------------------------------------------------------------------------

export default function AIChatPanel() {
  const dispatch = useAppDispatch();
  const isAIEnabled = useIsAIEnabled();
  const isOpen = useAppSelector((s) => s.aiChat.isOpen);
  // A provider error (rate limit, overloaded model, rejected key) used to set
  // `lastError` and be rendered NOWHERE — the user just got an empty bubble
  // and called the assistant stuck. It is a banner now.
  const lastError = useAppSelector((s) => s.aiChat.lastError);
  useAIHotkey();

  if (!isAIEnabled) return null;

  return (
    <VoiceProvider>
      {/* Click-outside backdrop (optional — keep light so the user still sees content behind) */}
      {isOpen && (
        <div
          onClick={() => dispatch(closePanel())}
          style={{
            position: "fixed",
            inset: 0,
            background: "transparent",
            zIndex: 49,
          }}
        />
      )}

      {/* Panel */}
      <aside
        aria-hidden={!isOpen}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          // --screen-h cancels the global body zoom so the panel fills the
          // real viewport height (see variables.css).
          height: "var(--screen-h, 100vh)",
          width: 400,
          maxWidth: "92vw",
          background: "var(--color-bg, #fff)",
          borderLeft: "1px solid var(--color-black-3, #e5e7eb)",
          boxShadow: "-12px 0 32px rgba(0,0,0,0.08)",
          transform: isOpen ? "translateX(0)" : "translateX(110%)",
          transition: "transform 220ms cubic-bezier(0.32, 0.72, 0, 1)",
          zIndex: 50,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <AIPanelHeader />

        <AIMessageList />
        {lastError && (
          <div
            role="alert"
            style={{
              margin: "0 12px 8px",
              padding: "10px 12px",
              borderRadius: 10,
              fontSize: 12.5,
              lineHeight: 1.5,
              color: "var(--color-error, #da2a46)",
              background: "rgba(218, 42, 70, 0.08)",
              border: "1px solid rgba(218, 42, 70, 0.25)",
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
            }}
          >
            <span aria-hidden>⚠️</span>
            <span style={{ flex: 1, minWidth: 0 }}>{lastError}</span>
            <button
              onClick={() => dispatch(setError(null))}
              aria-label="Dismiss"
              style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontSize: 14, lineHeight: 1, padding: 2 }}
            >
              ×
            </button>
          </div>
        )}
        <AIComposer />
      </aside>
      {/* Full-screen live voice call (Gemini Live) — Malayalam by default. */}
      <VoiceCallOverlay />
    </VoiceProvider>
  );
}
